import { z } from 'zod';
const CmsQuestionBase = z.object({
    slug: z.string(),
    text: z.string(),
    introLessonText: z.string(),
    indexInExercise: z.number(),
    hints: z.array(z.string()).nullable().optional(),
    modelAnswer: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
});
const FreeTextQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT'),
});
const FreeTextAIVerifiedQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_AI_VERIFIED'),
});
const FreeTextSelfCheckQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_SELF_CHECK'),
    googleSheetsLink: z.string().nullable().optional(),
    tableauLink: z.string().nullable().optional(),
});
const StrictTextQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('STRICT_TEXT'),
    strictTextAnswers: z.array(z.string()),
});
const SQLPlaygroundQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('PLAYGROUND'),
    setupSQL: z.string(),
    answerDQL: z.string().nullable().optional(),
    verificationSQL: z.string().nullable().optional(),
});
const FreeTextSQLPlaygroundQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_PLAYGROUND'),
    setupSQL: z.string(),
});
const FreeTextSQLCodeQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_SQL_CODE'),
});
const PythonValidationFields = {
    outputRegexesPy: z.array(z.string()).nullable().optional(),
    inputRegexesPy: z.array(z.string()).nullable().optional(),
    inputsExamplesPy: z.array(z.string()).nullable().optional(),
    expectErrorPy: z.boolean().nullable().optional(),
};
const PythonPlaygroundQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('PYTHON_PLAYGROUND'),
    initialPy: z.string().nullable().optional(),
    setupPy: z.string().nullable().optional(),
    ...PythonValidationFields,
});
const PythonMultipleChoiceQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('PYTHON_MULTIPLE_CHOICE'),
    initialPy: z.string().nullable().optional(),
    setupPy: z.string().nullable().optional(),
    multipleChoiceAnswers: z.object({
        correctAnswer: z.string(),
        distractors: z.array(z.string()),
    }),
    ...PythonValidationFields,
});
const SQLMultipleChoiceQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('SQL_MULTIPLE_CHOICE'),
    setupSQL: z.string(),
    multipleChoiceAnswers: z.object({
        correctAnswer: z.string(),
        distractors: z.array(z.string()),
    }),
});
const LLMPlaygroundQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('LLM_PLAYGROUND'),
    llmInitialPrompt: z.string().nullable().optional(),
});
const LookerExternalQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('LOOKER_EXTERNAL'),
    lookerLink: z.string(),
});
const FreeTextLookerEmbedQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_LOOKER_EMBED'),
    lookerLink: z.string(),
});
const TableauExternalQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('TABLEAU_EXTERNAL'),
    tableauLink: z.string(),
});
const FreeTextTableauEmbedQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_TABLEAU_EMBED'),
    tableauLink: z.string(),
});
const SheetsExternalQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('GOOGLE_SHEETS_EXTERNAL'),
    googleSheetsLink: z.string(),
});
const FreeTextSheetsEmbedQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_GOOGLE_SHEETS_EXTERNAL'),
    googleSheetsLink: z.string(),
});
const FreeTextPowerBIExternalQuestionSchema = CmsQuestionBase.extend({
    type: z.literal('FREE_TEXT_POWERBI_EXTERNAL'),
    powerbiCSV: z.string(),
});
export const CmsProjectQuestionSchema = z.discriminatedUnion('type', [
    FreeTextQuestionSchema,
    FreeTextAIVerifiedQuestionSchema,
    FreeTextSelfCheckQuestionSchema,
    StrictTextQuestionSchema,
    SQLPlaygroundQuestionSchema,
    FreeTextSQLPlaygroundQuestionSchema,
    FreeTextSQLCodeQuestionSchema,
    PythonPlaygroundQuestionSchema,
    PythonMultipleChoiceQuestionSchema,
    SQLMultipleChoiceQuestionSchema,
    LLMPlaygroundQuestionSchema,
    LookerExternalQuestionSchema,
    FreeTextLookerEmbedQuestionSchema,
    TableauExternalQuestionSchema,
    FreeTextTableauEmbedQuestionSchema,
    SheetsExternalQuestionSchema,
    FreeTextSheetsEmbedQuestionSchema,
    FreeTextPowerBIExternalQuestionSchema,
]);
export const AppliedProjectTagSchema = z.object({
    value: z.string(),
    type: z.enum(['TOPIC', 'ROLE', 'APPLICATION']),
});
export const ObjectiveSchema = z.object({
    how: z.string(),
    skill: z.string(),
    weight: z.number(),
    objective: z.string(),
    isCritical: z.boolean(),
});
export const CmsProjectDefinitionSchema = z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
    topic: z.string().min(1),
    description: z.string(),
    summary: z.string(),
    lesson: z.string(),
    tags: z.array(AppliedProjectTagSchema).default([]),
    imageUrl: z.string().nullable().optional(),
    objectives: z.array(ObjectiveSchema).default([]),
    publishStatus: z.enum(['LIVE', 'ARCHIVED']).optional(),
    isProsumer: z.boolean().default(true),
    questions: z.array(CmsProjectQuestionSchema),
});
